/**
 * The siding questions: what the house gets, what colour, how many of each.
 *
 * Asked before a siding estimate is generated (siding.ts has the walls and
 * the arithmetic). Jonathon, 2026-09-30: "template should ask rep before
 * generating the estimate if we're going to be replacing the soffit, fascia,
 * wrapping doors and windows", and the vent and block counts "just need to
 * pop up" for the rep to edit. Nothing here touches money.
 */

import {
  fasciaLf,
  friezeLf,
  sidingSquaresOf,
  type WallMeasurements,
} from "./siding.js";

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/* -------------------------------------------------------------------------
 * The questions
 * ---------------------------------------------------------------------- */

/**
 * The counts a rep confirms before a siding estimate is generated.
 *
 * Vents, light blocks, split blocks and a dryer vent are a fact about the
 * house that no report counts reliably. Jonathon, 2026-09-30, asked whether
 * the template should ask or default: "rep will edit. just need to pop up."
 * So the form shows them with a starting number and the rep corrects it.
 *
 * Each key is a `count_source` a template line can carry, and the line's
 * quantity is the answer. Never renamed: they are stored on template lines.
 */
export const SIDING_COUNT_SOURCES = [
  "octagon_vent",
  "rectangle_vent",
  "light_block",
  "split_block",
  "dryer_vent",
] as const;
export type SidingCountSource = (typeof SIDING_COUNT_SOURCES)[number];

export const SIDING_COUNT_LABELS: Record<SidingCountSource, string> = {
  octagon_vent: "Octagon gable vents",
  rectangle_vent: "Rectangle gable vents",
  light_block: "Light blocks (mount masters)",
  split_block: "Split blocks (pipes and wires)",
  dryer_vent: "Hooded dryer vents",
};

/** Every answer the siding form collects before it generates. */
export interface SidingAnswers {
  /**
   * The old siding comes off. Off, the new siding goes over what is there
   * (a layover). Jonathon, 2026-09-30: "per square of siding for layover and
   * another square charge for removal."
   */
  removal: boolean;
  /** Wrap the windows and doors in coil, charged each. */
  wrapOpenings: boolean;
  /** Replace the soffit with vinyl, by the foot. */
  soffit: boolean;
  /** Wrap the fascia in coil, by the foot. */
  fascia: boolean;
  /** Wrap the frieze board in coil, by the foot. */
  frieze: boolean;
  /** The panel colour — one of Parkview's eighteen, or whatever was typed. */
  panelColor: string;
  /** The trim and accessory colour. Often the panel's; sometimes white. */
  trimColor: string;
  counts: Record<SidingCountSource, number>;
}

/**
 * What the form starts with: a layover with nothing optional ticked, the
 * vents Hover counted, and nothing else.
 *
 * Hover counts every vent as one number, and the rectangle gable vent is the
 * common one, so that is where its count lands. Everything else starts at
 * zero because a zero is a question the rep answers, where a guess is a
 * number nobody looks at twice.
 */
export function defaultSidingAnswers(
  walls: readonly WallMeasurements[] = [],
): SidingAnswers {
  const vents = walls.reduce((total, wall) => total + num(wall.vent_qty), 0);
  return {
    removal: false,
    wrapOpenings: false,
    soffit: false,
    fascia: false,
    frieze: false,
    panelColor: "",
    trimColor: "",
    counts: {
      octagon_vent: 0,
      rectangle_vent: Math.max(0, Math.round(vents)),
      light_block: 0,
      split_block: 0,
      dryer_vent: 0,
    },
  };
}

/**
 * The tokens a set of answers satisfies — see `lineApplies` in flatRoof.ts.
 *
 * `layover` and `removal` are both named so a template can carry a line for
 * either: the install rate is the same, but a tear-off adds the removal
 * charge, and a layover might one day add furring or a levelling product.
 */
export function sidingTokens(answers: SidingAnswers): ReadonlySet<string> {
  const tokens = new Set<string>();
  tokens.add(answers.removal ? "removal" : "layover");
  if (answers.wrapOpenings) tokens.add("wrap_openings");
  if (answers.soffit) tokens.add("soffit");
  if (answers.fascia) tokens.add("fascia");
  if (answers.frieze) tokens.add("frieze");
  return tokens;
}

/** The `applies_when` prefixes a siding template uses. */
export const SIDING_TOKEN_PREFIXES: readonly string[] = [
  "layover",
  "removal",
  "wrap_openings",
  "soffit",
  "fascia",
  "frieze",
];

/** A template line, as far as the counted accessories are concerned. */
export interface CountedLine {
  qty?: unknown;
  count_source?: string | null;
}

/**
 * The same lines with the rep's counts written in as quantities.
 *
 * A line that names a count source takes the answer for it, and an answer of
 * zero drops the line: no dryer vent on the order for a house with no dryer.
 * Lines naming no source pass through untouched, qty and all.
 */
export function withSidingCounts<L extends CountedLine>(
  items: readonly L[],
  answers: SidingAnswers,
): L[] {
  const out: L[] = [];
  for (const item of items) {
    const source = item.count_source ?? null;
    if (
      !source ||
      !(SIDING_COUNT_SOURCES as readonly string[]).includes(source)
    ) {
      out.push(item);
      continue;
    }
    const count = Math.max(
      0,
      Math.round(num(answers.counts[source as SidingCountSource])),
    );
    if (count <= 0) continue;
    out.push({ ...item, qty: count });
  }
  return out;
}

/* -------------------------------------------------------------------------
 * The colours
 * ---------------------------------------------------------------------- */

/** A product, as far as colouring a siding order is concerned. */
export interface SidingColorProduct {
  id: string;
  color_rule?: string | null;
}

/** The slot whose coloured products are the siding itself. */
export const SIDING_PANEL_SLOT = "siding";

/**
 * The products that are the siding — everything in the Siding section.
 *
 * Read off the template's lines rather than guessed from a name: the panel
 * is whatever the template hangs in that section, and the crew's install
 * line sits there too, which `sidingLineColor` leaves blank because the
 * catalogue gives it no colour rule.
 */
export function sidingPanelProductIds(
  items: readonly { slot: string; product_id: string | null }[],
): string[] {
  return items
    .filter((item) => item.slot === SIDING_PANEL_SLOT && item.product_id)
    .map((item) => item.product_id as string);
}

/**
 * The colour a generated siding line carries onto the material order.
 *
 * Jonathon, 2026-09-30, asked whether the template asks for a panel colour and
 * a trim colour and carries them onto the order: "yes". The siding itself —
 * the products in the Siding section — takes the panel colour; every other
 * product that the catalogue says needs a colour takes the trim colour. A
 * product with no colour rule (starter, house wrap, tape, the crew's install)
 * stays blank, as it does today. An empty answer leaves the line blank too,
 * and the order's own colour gate holds it until somebody fills it in.
 */
export function sidingLineColor(
  product: SidingColorProduct | undefined,
  panelProductIds: readonly string[],
  answers: Pick<SidingAnswers, "panelColor" | "trimColor">,
): string | null {
  if (!product) return null;
  if ((product.color_rule ?? "") !== "required") return null;
  if (panelProductIds.includes(product.id)) {
    return answers.panelColor.trim() || null;
  }
  return answers.trimColor.trim() || null;
}

/* -------------------------------------------------------------------------
 * The scope of work
 * ---------------------------------------------------------------------- */

/**
 * The sentences the answers add to the scope the customer reads.
 *
 * Jonathon, 2026-09-30: "template should ask rep before generating the
 * estimate if we're going to be replacing the soffit, fascia, wrapping doors
 * and windows." What was asked and answered yes is promised; what was answered
 * no is said not to be included, so the customer is not left to assume.
 */
export function sidingScopeLines(answers: SidingAnswers): string[] {
  return [
    answers.removal
      ? "Remove and dispose of the existing siding before the new siding goes on."
      : "The new siding is installed over the existing siding; nothing is torn off.",
    answers.wrapOpenings
      ? "Wrap the window and door trim in color-matched aluminum coil."
      : "Window and door trim is not wrapped.",
    ...trimSentences(answers),
    ...colourSentence(answers),
  ];
}

/** "a, b and c" from a list. */
const spoken = (parts: readonly string[]): string =>
  parts.join(", ").replace(/, ([^,]*)$/, " and $1");

/** What is done to the soffit, fascia and frieze, and what is left alone. */
function trimSentences(answers: SidingAnswers): string[] {
  const lines: string[] = [];
  const done: string[] = [];
  if (answers.soffit) done.push("replace the soffit with vented vinyl soffit");
  if (answers.fascia) done.push("wrap the fascia in aluminum coil");
  if (answers.frieze) done.push("wrap the frieze board in aluminum coil");
  if (done.length > 0) {
    const sentence = spoken(done);
    lines.push(sentence.charAt(0).toUpperCase() + sentence.slice(1) + ".");
  }
  const left = [
    answers.soffit ? null : "soffit",
    answers.fascia ? null : "fascia",
    answers.frieze ? null : "frieze board",
  ].filter((part): part is string => part !== null);
  if (left.length > 0) {
    const one = left.length === 1;
    lines.push(
      `The existing ${spoken(left)} ${one ? "stays" : "stay"} as ${one ? "it is" : "they are"}.`,
    );
  }
  return lines;
}

/** The colours, when a panel colour was chosen. */
function colourSentence(answers: SidingAnswers): string[] {
  const panel = answers.panelColor.trim();
  const trim = answers.trimColor.trim();
  if (panel === "") return [];
  const trimPart =
    trim !== "" && trim !== panel ? `; trim color: ${trim}.` : ".";
  return [`Siding color: ${panel}${trimPart}`];
}

/** The template's scope, with the answers' sentences under it. */
export function sidingScopeNote(
  templateScope: string | null | undefined,
  answers: SidingAnswers,
): string | null {
  const own = (templateScope ?? "").trim();
  const added = sidingScopeLines(answers)
    .map((line) => `• ${line}`)
    .join("\n");
  return [own, added].filter((part) => part !== "").join("\n\n") || null;
}

/* -------------------------------------------------------------------------
 * What to say before generating
 * ---------------------------------------------------------------------- */

/**
 * What is missing from the wall, said before an estimate is generated from it.
 *
 * The same reason the flat roof form checks its measurements: a wall with no
 * siding area generates a template whose every line is nothing, and the
 * result reads as an estimate until somebody counts the lines.
 */
export function sidingMeasurementWarnings(
  answers: SidingAnswers,
  walls: readonly WallMeasurements[],
): string[] {
  const warnings: string[] = [];
  if (walls.length === 0) return warnings;
  const siding = walls.reduce((t, w) => t + sidingSquaresOf(w), 0);
  if (siding <= 0) {
    warnings.push(
      "No siding area is measured on this job, so the panels, house wrap and the crew's install come out at nothing.",
    );
  }
  if (answers.wrapOpenings && walls.every((w) => num(w.openings_qty) <= 0)) {
    warnings.push(
      "Windows and doors are to be wrapped, but none are counted on the wall measurement.",
    );
  }
  if (answers.soffit && walls.every((w) => num(w.soffit_lf) <= 0)) {
    warnings.push(
      "Soffit is to be replaced, but no soffit length is measured.",
    );
  }
  if (answers.fascia && walls.every((w) => fasciaLf(w) <= 0)) {
    warnings.push("Fascia is to be wrapped, but no fascia length is measured.");
  }
  if (answers.frieze && walls.every((w) => friezeLf(w) <= 0)) {
    warnings.push(
      "Frieze board is to be wrapped, but no frieze length is measured.",
    );
  }
  return warnings;
}
